import { makeStyles } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";
import { sharedAnimations } from "theme/sharedStyles";

export const useMyChatsStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100dvh",
    backgroundColor: designTokens.neutral.white,
    padding: designTokens.spacing[6],
    gap: designTokens.spacing[4],
    overflow: "hidden",
    boxSizing: "border-box",
    "@media (max-width: 640px)": {
      padding: designTokens.spacing[3],
      gap: designTokens.spacing[2],
    },
    "@media (max-width: 768px)": {
      paddingTop: designTokens.spacing[4],
    },
    "@media (max-width: 375px)": {
      padding: designTokens.spacing[2],
      paddingTop: designTokens.spacing[4],
    },
  },
  innerContainer: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "900px",
    width: "100%",
    height: "100%",
    minHeight: 0,
    marginLeft: "auto",
    marginRight: "auto",
    gap: designTokens.spacing[4],
    animationName: sharedAnimations.fadeIn,
    animationDuration: designTokens.transition.base,
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationName: "none",
    },
    "@media (max-width: 640px)": {
      gap: designTokens.spacing[2],
    },
    "@media (min-width: 641px) and (max-width: 1024px)": {
      maxWidth: "100%",
    },
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    "@media (max-width: 768px)": {
      justifyContent: "flex-end",
      minHeight: "44px",
      paddingLeft: "60px",
    },
  },
  headerTitle: {
    "@media (max-width: 768px)": {
      display: "none",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "0",
    backgroundColor: designTokens.neutral.white,
    overflowY: "auto",
    overflowX: "hidden",
    flex: 1,
    minHeight: 0,
    maxHeight: "600px",
    scrollBehavior: "smooth",
    "@media (prefers-reduced-motion: reduce)": {
      scrollBehavior: "auto",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: designTokens.spacing[8],
    color: designTokens.neutral[500],
    animationName: sharedAnimations.fadeIn,
    animationDuration: designTokens.transition.slow,
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationName: "none",
    },
  },
  errorContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: designTokens.spacing[8],
    color: designTokens.semantic.error,
    animationName: sharedAnimations.fadeSlideUp,
    animationDuration: designTokens.transition.base,
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationName: "none",
    },
  },
  loadingMore: {
    display: "flex",
    justifyContent: "center",
    padding: designTokens.spacing[4],
    color: designTokens.neutral[500],
    fontSize: designTokens.fontSize.sm,
    animationName: sharedAnimations.fadeIn,
    animationDuration: designTokens.transition.fast,
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationName: "none",
    },
  },
  searchInput: {
    width: "100%",
    maxWidth: "100%",
    flexShrink: 0,
    borderRadius: designTokens.borderRadius.lg,
    border: `1px solid ${designTokens.neutral[200]}`,
    backgroundColor: designTokens.neutral.white,
    transition: `all ${designTokens.transition.fast}`,
    "::after": {
      display: "none",
    },
    ":focus-within": {
      borderTopColor: designTokens.accent[500],
      borderRightColor: designTokens.accent[500],
      borderBottomColor: designTokens.accent[500],
      borderLeftColor: designTokens.accent[500],
      boxShadow: "0 0 0 3px rgba(3, 159, 184, 0.1)",
      outline: "none",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: `${designTokens.spacing[12]} ${designTokens.spacing[6]}`,
    color: designTokens.neutral[500],
    textAlign: "center",
    gap: designTokens.spacing[2],
    animationName: sharedAnimations.fadeSlideUp,
    animationDuration: designTokens.transition.slow,
    animationFillMode: "both",
    "@media (prefers-reduced-motion: reduce)": {
      animationName: "none",
    },
    "@media (max-width: 640px)": {
      padding: `${designTokens.spacing[8]} ${designTokens.spacing[3]}`,
    },
  },
  backToTopButton: {
    position: "absolute",
    bottom: designTokens.spacing[4],
    right: designTokens.spacing[4],
    width: "40px",
    height: "40px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: designTokens.neutral[800],
    color: designTokens.neutral.white,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: designTokens.shadow.lg,
    transition: `all ${designTokens.transition.fast}`,
    opacity: 0,
    transform: "translateY(8px)",
    pointerEvents: "none",
    zIndex: 1,
    ":hover": {
      backgroundColor: designTokens.neutral[900],
      transform: "translateY(-2px)",
      boxShadow: designTokens.shadow.xl,
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "0ms",
    },
    "@media (max-width: 640px)": {
      bottom: designTokens.spacing[3],
      right: designTokens.spacing[3],
      width: "44px",
      height: "44px",
    },
  },
  backToTopVisible: {
    opacity: 1,
    transform: "translateY(0)",
    pointerEvents: "auto",
  },
  contentWrapper: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
  },
  skeletonItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[3]}`,
    margin: `${designTokens.spacing[1]} 0`,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.lg,
    gap: designTokens.spacing[3],
  },
  skeletonIcon: {
    width: "24px",
    height: "24px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: designTokens.neutral[100],
  },
  skeletonContent: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    gap: designTokens.spacing[2],
  },
  skeletonTitle: {
    height: "14px",
    width: "60%",
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: designTokens.neutral[100],
  },
  skeletonMeta: {
    height: "10px",
    width: "40%",
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: designTokens.neutral[100],
  },
});
